import { useState } from 'react'
import {
  Download,
  Copy,
  Check,
  FileText,
  FileJson,
  FileCode,
  GitBranch,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { Modal, Button } from '@/components/common'
import { useExport } from '@/hooks/useExport'
import type { ExportFormat } from './ExportButtons'

const formatMeta: Record<
  ExportFormat,
  { icon: React.ElementType; label: string; extension: string; description: string }
> = {
  latex: {
    icon: FileText,
    label: 'LaTeX',
    extension: '.tex',
    description: 'Academic paper format — compile with pdflatex.',
  },
  markdown: {
    icon: FileText,
    label: 'Markdown',
    extension: '.md',
    description: 'Universal text format with full AI3C breakdown.',
  },
  json: {
    icon: FileJson,
    label: 'JSON',
    extension: '.json',
    description: 'Full conversation + problem output for downstream tooling.',
  },
  mermaid: {
    icon: GitBranch,
    label: 'Mermaid',
    extension: '.mmd',
    description: 'Tree diagram you can paste into mermaid.live.',
  },
  graphviz: {
    icon: FileCode,
    label: 'GraphViz',
    extension: '.dot',
    description: 'DOT diagram for graphviz/digraph rendering.',
  },
}

export interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  projectName: string
}

export const ExportModal = ({
  isOpen,
  onClose,
  projectId,
  projectName,
}: ExportModalProps) => {
  const exporter = useExport({ projectId })
  const [recentlyDownloaded, setRecentlyDownloaded] = useState<ExportFormat | null>(null)
  const [recentlyCopied, setRecentlyCopied] = useState<ExportFormat | null>(null)

  const handleDownload = async (format: ExportFormat) => {
    const result = await exporter.exportAs(format)
    if (!result) {
      toast.error(exporter.error ?? 'Export failed')
      return
    }
    exporter.downloadExport(result)
    setRecentlyDownloaded(format)
    toast.success(`Downloaded ${result.filename}`)
    setTimeout(() => setRecentlyDownloaded(null), 2500)
  }

  const handleCopy = async (format: ExportFormat) => {
    const result = await exporter.exportAs(format)
    if (!result) {
      toast.error(exporter.error ?? 'Export failed')
      return
    }
    const ok = await exporter.copyToClipboard(result)
    if (ok) {
      setRecentlyCopied(format)
      toast.success(`Copied ${formatMeta[format].label} to clipboard`)
      setTimeout(() => setRecentlyCopied(null), 2500)
    } else {
      toast.error('Could not access clipboard')
    }
  }

  const formats: ExportFormat[] = ['latex', 'markdown', 'json', 'mermaid', 'graphviz']

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Export "${projectName}"`}
      description="Choose a format. Files are generated server-side from the latest problem output."
      size="lg"
    >
      <div className="space-y-3">
        <div className="divide-y divide-slate-200 dark:divide-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          {formats.map((format) => {
            const meta = formatMeta[format]
            const Icon = meta.icon
            const isExportingThis =
              exporter.isExporting && exporter.exportingFormat === format
            return (
              <div
                key={format}
                className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-800/40"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-700 flex-shrink-0">
                  {isExportingThis ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-white">
                      {meta.label}
                    </span>
                    <span className="text-xs text-slate-400">{meta.extension}</span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {meta.description}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={
                      recentlyCopied === format ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )
                    }
                    onClick={() => handleCopy(format)}
                    disabled={exporter.isExporting}
                  >
                    {recentlyCopied === format ? 'Copied' : 'Copy'}
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={
                      recentlyDownloaded === format ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )
                    }
                    onClick={() => handleDownload(format)}
                    disabled={exporter.isExporting}
                  >
                    {recentlyDownloaded === format ? 'Saved' : 'Download'}
                  </Button>
                </div>
              </div>
            )
          })}
        </div>

        {exporter.error && (
          <p className="text-sm text-red-600 dark:text-red-400 px-1">
            {exporter.error}
          </p>
        )}
      </div>
    </Modal>
  )
}

export default ExportModal
